import React, { useState, useEffect, useCallback } from 'react';
import { IconChartArrowsVertical, IconPlus, IconEye, IconX, IconPhone, IconCoin, IconUser, IconClock, IconNote, IconSend, IconEdit, IconTrash, IconCheck } from '@tabler/icons-react';
import { AGENTS } from '../data/industries';
import Contacts, { Tags as TagsTabView, Groups as GroupsView } from './Contacts';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const PRESET_COLORS = ['#6B7280', '#1B6B93', '#B5651D', '#7B2D5F', '#22C55E', '#EF4444', '#D97706', '#6366F1'];

const PRIORITIES = [
  { key: 'low', labelEn: 'Low', labelAr: 'منخفض', color: '#9CA3AF' },
  { key: 'medium', labelEn: 'Medium', labelAr: 'متوسط', color: '#D97706' },
  { key: 'high', labelEn: 'High', labelAr: 'عالي', color: '#EF4444' },
];

const AGENT_KEYS = Object.keys(AGENTS);

function buildDefaultStages() {
  return [
    { key: 'new', labelEn: 'New', labelAr: 'جديد', color: '#6B7280' },
    { key: 'qualified', labelEn: 'Qualified', labelAr: 'مؤهل', color: '#1B6B93' },
    { key: 'proposal', labelEn: 'Proposal', labelAr: 'عرض سعر', color: '#B5651D' },
    { key: 'negotiation', labelEn: 'Negotiation', labelAr: 'تفاوض', color: '#7B2D5F' },
    { key: 'won', labelEn: 'Won', labelAr: 'مكسوبة', color: '#22C55E' },
  ];
}

let dealIdCounter = 0;
function ensureDealIds(deals) {
  return deals.map(d => d._id ? d : { ...d, _id: `deal-${dealIdCounter++}` });
}

function DealSlideOver({ deal, lang, stages, onClose, onAddNote, onUpdateDeal }) {
  const [noteText, setNoteText] = useState('');
  const isRtl = lang === 'ar';
  const agent = AGENTS[deal.agent];
  const stage = stages.find(s => s.key === deal.stage) || { labelEn: deal.stage, labelAr: deal.stage, color: '#6B7280' };
  const priority = PRIORITIES.find(p => p.key === deal.priority) || PRIORITIES[0];
  const notes = deal.notes || [];
  const history = deal.history || [];

  function handleAddNote() {
    const text = noteText.trim();
    if (!text) return;
    onAddNote(deal._id, text);
    setNoteText('');
  }

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200,
          animation: 'fadeIn 0.15s ease'
        }}
      />
      <div style={{
        position: 'fixed', top: 0, bottom: 0,
        [isRtl ? 'left' : 'right']: 0,
        width: 400, background: '#fff', zIndex: 201,
        boxShadow: isRtl ? '4px 0 24px rgba(0,0,0,0.15)' : '-4px 0 24px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column',
        animation: 'slideRight 0.25s ease'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexDirection: isRtl ? 'row-reverse' : 'row'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10,
            flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: stage?.color + '20',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <IconCoin size={18} color={stage?.color} />
            </div>
            <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#1A1A2E' }}>
                {isRtl ? deal.nameAr : deal.nameEn}
              </div>
              <div className="font-mono" style={{ fontSize: 14, fontWeight: 700, color: stage?.color }}>
                {isRtl ? deal.valueAr : deal.valueEn}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#9CA3AF', padding: 4, borderRadius: 6,
            display: 'flex', alignItems: 'center'
          }}>
            <IconX size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {/* Customer Info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
            {deal.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <IconPhone size={15} color="#6B7280" />
                <span style={{ fontSize: 13, color: '#374151', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {deal.phone}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10,
              flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              <IconUser size={15} color={agent?.color} />
              <span style={{ fontSize: 13, color: agent?.color, fontWeight: 500 }}>
                {isRtl ? agent?.nameAr : agent?.name} — {isRtl ? agent?.roleAr : agent?.role}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10,
              flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: stage?.color, marginLeft: 3, marginRight: 3 }} />
              <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
                {isRtl ? stage?.labelAr : stage?.labelEn}
              </span>
              <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                {isRtl ? deal.dateAr : deal.dateEn}
              </span>
            </div>
          </div>

          {/* Assign & Priority */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            {/* Assign to */}
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em',
                textTransform: 'uppercase', marginBottom: 6, textAlign: isRtl ? 'right' : 'left'
              }}>
                {isRtl ? 'المسؤول' : 'ASSIGNED TO'}
              </div>
              <select
                value={deal.agent}
                onChange={e => onUpdateDeal(deal._id, { agent: e.target.value })}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8,
                  border: '1px solid #E5E7EB', fontSize: 13, fontFamily: 'inherit',
                  background: '#fff', cursor: 'pointer', color: AGENTS[deal.agent]?.color,
                  fontWeight: 600, direction: isRtl ? 'rtl' : 'ltr', outline: 'none'
                }}
              >
                {AGENT_KEYS.map(ak => (
                  <option key={ak} value={ak}>
                    {isRtl ? AGENTS[ak].nameAr : AGENTS[ak].name} — {isRtl ? AGENTS[ak].roleAr : AGENTS[ak].role}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em',
                textTransform: 'uppercase', marginBottom: 6, textAlign: isRtl ? 'right' : 'left'
              }}>
                {isRtl ? 'الأولوية' : 'PRIORITY'}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {PRIORITIES.map(p => (
                  <button
                    key={p.key}
                    onClick={() => onUpdateDeal(deal._id, { priority: p.key })}
                    style={{
                      flex: 1, padding: '7px 0', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      fontFamily: 'inherit', cursor: 'pointer', transition: 'all 0.15s',
                      background: deal.priority === p.key ? p.color + '18' : '#F9FAFB',
                      color: deal.priority === p.key ? p.color : '#9CA3AF',
                      border: deal.priority === p.key ? `1.5px solid ${p.color}` : '1.5px solid #E5E7EB'
                    }}
                  >
                    {isRtl ? p.labelAr : p.labelEn}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em',
              textTransform: 'uppercase', marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 6,
              flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <IconNote size={13} />
              {isRtl ? 'الملاحظات' : 'NOTES'}
            </div>

            {notes.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {notes.map((note, idx) => (
                  <div key={idx} style={{
                    padding: '10px 12px', borderRadius: 8, background: '#F9FAFB',
                    border: '1px solid #F3F4F6'
                  }}>
                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5,
                      textAlign: isRtl ? 'right' : 'left' }}>
                      {isRtl ? note.textAr : note.textEn}
                    </div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4,
                      textAlign: isRtl ? 'right' : 'left' }}>
                      {isRtl ? note.dateAr : note.dateEn}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add note input */}
            <div style={{ display: 'flex', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              <textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder={isRtl ? 'أضف ملاحظة...' : 'Add a note...'}
                style={{
                  flex: 1, padding: '10px 12px', borderRadius: 8,
                  border: '1px solid #E5E7EB', fontSize: 13,
                  fontFamily: 'inherit', resize: 'vertical', minHeight: 40, maxHeight: 120,
                  outline: 'none', direction: isRtl ? 'rtl' : 'ltr'
                }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
              />
              <button
                onClick={handleAddNote}
                disabled={!noteText.trim()}
                style={{
                  padding: '10px 12px', borderRadius: 8,
                  background: noteText.trim() ? '#5B2D8E' : '#E5E7EB',
                  color: noteText.trim() ? '#fff' : '#9CA3AF',
                  border: 'none', cursor: noteText.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', alignSelf: 'flex-end'
                }}
              >
                <IconSend size={16} />
              </button>
            </div>
          </div>

          {/* History Section */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em',
              textTransform: 'uppercase', marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 6,
              flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <IconClock size={13} />
              {isRtl ? 'السجل' : 'HISTORY'}
            </div>

            {history.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {history.map((entry, idx) => (
                  <div key={idx} style={{
                    display: 'flex', gap: 12, padding: '8px 0',
                    flexDirection: isRtl ? 'row-reverse' : 'row'
                  }}>
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0
                    }}>
                      <div style={{
                        width: 8, height: 8, borderRadius: '50%', background: '#5B2D8E',
                        marginTop: 5
                      }} />
                      {idx < history.length - 1 && (
                        <div style={{ width: 1, flex: 1, background: '#E5E7EB', marginTop: 4 }} />
                      )}
                    </div>
                    <div style={{ paddingBottom: 8, textAlign: isRtl ? 'right' : 'left' }}>
                      <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
                        {isRtl ? entry.actionAr : entry.actionEn}
                      </div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                        {isRtl ? entry.dateAr : entry.dateEn}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#D1D5DB', textAlign: isRtl ? 'right' : 'left' }}>
                {isRtl ? 'لا يوجد سجل بعد' : 'No history yet'}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default function CRM({ lang, t, industry, demoPhase }) {
  const [deals, setDeals] = useState(() => ensureDealIds(industry.deals || []));
  const [stages, setStages] = useState(buildDefaultStages);
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [editingStageKey, setEditingStageKey] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editColor, setEditColor] = useState('');
  const [deletingStageKey, setDeletingStageKey] = useState(null);
  const [moveToStage, setMoveToStage] = useState('');
  const [crmTab, setCrmTab] = useState('pipeline');
  const [filterAgent, setFilterAgent] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  // Lifted shared state for contacts/tags/segments/groups
  const [contacts, setContacts] = useState(industry.contacts || []);
  const [tags, setTags] = useState(industry.tags || []);
  const [segments, setSegments] = useState(industry.segments || []);
  const [groups, setGroups] = useState(industry.groups || []);

  const selectedDeal = selectedDealId ? deals.find(d => d._id === selectedDealId) : null;
  const filteredDeals = deals.filter(d =>
    (filterAgent === 'all' || d.agent === filterAgent) &&
    (filterPriority === 'all' || (d.priority || 'low') === filterPriority)
  );

  useEffect(() => {
    setDeals(ensureDealIds(industry.deals || []));
    setContacts(industry.contacts || []);
    setTags(industry.tags || []);
    setSegments(industry.segments || []);
    setGroups(industry.groups || []);
  }, [industry]);

  // When demo phase 1 completes, highlight new deal in qualified
  useEffect(() => {
    if (demoPhase >= 1 && industry.deals) {
      setDeals(ensureDealIds(industry.deals));
    }
  }, [demoPhase, industry]);

  const isRtl = lang === 'ar';

  const handleDragEnd = useCallback((result) => {
    const { destination, draggableId } = result;
    if (!destination) return;

    setDeals(prev => {
      const draggedIndex = prev.findIndex(d => d._id === draggableId);
      if (draggedIndex === -1) return prev;
      if (prev[draggedIndex].stage === destination.droppableId) return prev;
      const updated = [...prev];
      const newStage = stages.find(s => s.key === destination.droppableId);
      const stageLabel = newStage ? newStage.labelEn : destination.droppableId;
      const stageLabelAr = newStage ? newStage.labelAr : destination.droppableId;
      const historyEntry = {
        actionAr: `انتقل إلى ${stageLabelAr}`,
        actionEn: `Moved to ${stageLabel}`,
        dateAr: 'الآن', dateEn: 'Just now'
      };
      updated[draggedIndex] = {
        ...updated[draggedIndex],
        stage: destination.droppableId,
        history: [historyEntry, ...(updated[draggedIndex].history || [])]
      };
      return updated;
    });
  }, [stages]);

  const handleUpdateDeal = useCallback((dealId, updates) => {
    setDeals(prev => prev.map(d => d._id === dealId ? { ...d, ...updates } : d));
  }, []);

  const handleAddNote = useCallback((dealId, text) => {
    setDeals(prev => prev.map(d => {
      if (d._id !== dealId) return d;
      const newNote = { textAr: text, textEn: text, dateAr: 'الآن', dateEn: 'Just now' };
      return { ...d, notes: [...(d.notes || []), newNote] };
    }));
  }, []);

  function startEditStage(stageObj) {
    setEditingStageKey(stageObj.key);
    setEditLabel(isRtl ? stageObj.labelAr : stageObj.labelEn);
    setEditColor(stageObj.color);
    setDeletingStageKey(null);
  }

  function saveEditStage() {
    if (!editLabel.trim()) return;
    setStages(prev => prev.map(s => {
      if (s.key !== editingStageKey) return s;
      return isRtl
        ? { ...s, labelAr: editLabel.trim(), color: editColor }
        : { ...s, labelEn: editLabel.trim(), color: editColor };
    }));
    setEditingStageKey(null);
  }

  function startDeleteStage(stageKey) {
    setDeletingStageKey(stageKey);
    const otherStages = stages.filter(s => s.key !== stageKey);
    setMoveToStage(otherStages.length > 0 ? otherStages[0].key : '');
    setEditingStageKey(null);
  }

  function confirmDeleteStage() {
    if (!deletingStageKey || stages.length <= 1) return;
    if (moveToStage) {
      setDeals(prev => prev.map(d =>
        d.stage === deletingStageKey ? { ...d, stage: moveToStage } : d
      ));
    }
    setStages(prev => prev.filter(s => s.key !== deletingStageKey));
    setDeletingStageKey(null);
  }

  function addNewStage() {
    const key = `stage_${Date.now()}`;
    const newStage = {
      key,
      labelEn: 'New Stage',
      labelAr: 'مرحلة جديدة',
      color: PRESET_COLORS[stages.length % PRESET_COLORS.length]
    };
    setStages(prev => [...prev, newStage]);
    startEditStage(newStage);
  }

  return (
    <div style={{ animation: 'fadeIn 0.3s ease' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid #E5E7EB' }}>
        {['pipeline', 'contacts', 'groups', 'tags'].map(tab => {
          const tabLabels = {
            pipeline: t.pipeline || (isRtl ? 'خط الأعمال' : 'Pipeline'),
            contacts: t.contactsTab || (isRtl ? 'جهات الاتصال' : 'Contacts'),
            groups: t.groupsTab || (isRtl ? 'المجموعات' : 'Groups'),
            tags: t.tagsTab || (isRtl ? 'الوسوم' : 'Tags'),
          };
          return (
            <button
              key={tab}
              onClick={() => setCrmTab(tab)}
              style={{
                padding: '8px 20px', borderRadius: '8px 8px 0 0',
                background: crmTab === tab ? '#5B2D8E' : 'transparent',
                color: crmTab === tab ? '#fff' : '#6B7280',
                border: 'none', cursor: 'pointer', fontWeight: 600,
                fontSize: 13, fontFamily: 'inherit',
                marginBottom: crmTab === tab ? -2 : 0,
                borderBottom: crmTab === tab ? '2px solid #5B2D8E' : 'none',
                transition: 'all 0.15s'
              }}
            >
              {tabLabels[tab]}
            </button>
          );
        })}
      </div>

      {crmTab === 'contacts' ? (
        <Contacts lang={lang} t={t} industry={industry} contacts={contacts} setContacts={setContacts} tags={tags} setTags={setTags} segments={segments} setSegments={setSegments} groups={groups} setGroups={setGroups} />
      ) : crmTab === 'groups' ? (
        <GroupsView lang={lang} t={t} industry={industry} groups={groups} setGroups={setGroups} contacts={contacts} setContacts={setContacts} tags={tags} segments={segments} setSegments={setSegments} />
      ) : crmTab === 'tags' ? (
        <TagsTabView lang={lang} t={t} tags={tags} setTags={setTags} contacts={contacts} setContacts={setContacts} segments={segments} setSegments={setSegments} groups={groups} />
      ) : (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <span style={{ fontSize: 14, color: '#6B7280' }}>{t.pipelineValue}: </span>
          <span className="font-mono" style={{ fontSize: 20, fontWeight: 700, color: '#5B2D8E' }}>
            {industry.analytics.pipelineValue}
          </span>
        </div>
        <button style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
          background: '#5B2D8E', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13,
          fontWeight: 600, fontFamily: 'inherit'
        }}>
          <IconPlus size={16} /> {t.addDeal}
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap',
        flexDirection: isRtl ? 'row-reverse' : 'row', alignItems: 'center'
      }}>
        <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>
          {isRtl ? 'تصفية:' : 'Filter:'}
        </span>
        {/* Agent filter */}
        <select
          value={filterAgent}
          onChange={e => setFilterAgent(e.target.value)}
          style={{
            padding: '5px 10px', borderRadius: 8, border: '1px solid #E5E7EB',
            fontSize: 12, fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
            background: filterAgent !== 'all' ? '#F7F2FC' : '#fff',
            color: filterAgent !== 'all' ? '#5B2D8E' : '#374151',
            fontWeight: filterAgent !== 'all' ? 600 : 400,
            direction: isRtl ? 'rtl' : 'ltr'
          }}
        >
          <option value="all">{isRtl ? 'كل الفريق' : 'All Team'}</option>
          {AGENT_KEYS.map(ak => (
            <option key={ak} value={ak}>
              {isRtl ? AGENTS[ak].nameAr : AGENTS[ak].name}
            </option>
          ))}
        </select>
        {/* Priority filter */}
        <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
          <button
            onClick={() => setFilterPriority('all')}
            style={{
              padding: '5px 10px', border: 'none', fontSize: 12, fontFamily: 'inherit',
              cursor: 'pointer', fontWeight: filterPriority === 'all' ? 700 : 400,
              background: filterPriority === 'all' ? '#5B2D8E' : '#fff',
              color: filterPriority === 'all' ? '#fff' : '#6B7280'
            }}
          >
            {isRtl ? 'الكل' : 'All'}
          </button>
          {PRIORITIES.map(p => (
            <button
              key={p.key}
              onClick={() => setFilterPriority(p.key)}
              style={{
                padding: '5px 10px', border: 'none', borderLeft: '1px solid #E5E7EB',
                fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
                fontWeight: filterPriority === p.key ? 700 : 400,
                background: filterPriority === p.key ? `${p.color}18` : '#fff',
                color: filterPriority === p.key ? p.color : '#6B7280'
              }}
            >
              {isRtl ? p.labelAr : p.labelEn}
            </button>
          ))}
        </div>
        {(filterAgent !== 'all' || filterPriority !== 'all') && (
          <button
            onClick={() => { setFilterAgent('all'); setFilterPriority('all'); }}
            style={{
              padding: '5px 10px', borderRadius: 8, border: '1px solid #E5E7EB',
              background: '#fff', color: '#EF4444', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit'
            }}
          >
            {isRtl ? 'مسح' : 'Clear'}
          </button>
        )}
      </div>

      {/* Kanban columns */}
      <DragDropContext onDragEnd={handleDragEnd}>
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 20 }}>
        {stages.map(stageObj => {
          const stageKey = stageObj.key;
          const stageDeals = filteredDeals.filter(d => d.stage === stageKey);
          const isEditing = editingStageKey === stageKey;
          const isDeleting = deletingStageKey === stageKey;
          return (
            <Droppable droppableId={stageKey} key={stageKey}>
              {(colProvided, colSnapshot) => (
                <div
                  ref={colProvided.innerRef}
                  {...colProvided.droppableProps}
                  style={{
                    minWidth: 220, flex: 1, borderRadius: 12, padding: '12px',
                    border: colSnapshot.isDraggingOver ? '1px solid #5B2D8E' : '1px solid #E5E7EB',
                    background: colSnapshot.isDraggingOver ? '#F3EEFB' : '#F8F9FA',
                    transition: 'background 0.2s, border-color 0.2s'
                  }}
                >
                  {/* Column header */}
                  {isEditing ? (
                    <div style={{ marginBottom: 12, padding: '0 4px' }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                        <input
                          autoFocus
                          value={editLabel}
                          onChange={e => setEditLabel(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveEditStage(); if (e.key === 'Escape') setEditingStageKey(null); }}
                          style={{
                            flex: 1, fontSize: 14, fontWeight: 600, padding: '4px 8px',
                            borderRadius: 6, border: '1px solid #D1D5DB', outline: 'none',
                            fontFamily: 'inherit', direction: isRtl ? 'rtl' : 'ltr'
                          }}
                        />
                        <button onClick={saveEditStage} style={{
                          background: '#5B2D8E', color: '#fff', border: 'none', borderRadius: 6,
                          padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center'
                        }}>
                          <IconCheck size={14} />
                        </button>
                        <button onClick={() => setEditingStageKey(null)} style={{
                          background: '#F3F4F6', color: '#6B7280', border: 'none', borderRadius: 6,
                          padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center'
                        }}>
                          <IconX size={14} />
                        </button>
                      </div>
                      {/* Color picker */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {PRESET_COLORS.map(c => (
                          <button key={c} onClick={() => setEditColor(c)} style={{
                            width: 20, height: 20, borderRadius: '50%', background: c,
                            border: editColor === c ? '2px solid #1A1A2E' : '2px solid transparent',
                            cursor: 'pointer', padding: 0
                          }} />
                        ))}
                      </div>
                    </div>
                  ) : isDeleting ? (
                    <div style={{ marginBottom: 12, padding: '0 4px' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#EF4444', marginBottom: 6 }}>
                        {isRtl ? `حذف "${stageObj.labelAr}"؟` : `Delete "${stageObj.labelEn}"?`}
                      </div>
                      {stageDeals.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                            {isRtl ? `نقل ${stageDeals.length} صفقة إلى:` : `Move ${stageDeals.length} deal${stageDeals.length > 1 ? 's' : ''} to:`}
                          </div>
                          <select
                            value={moveToStage}
                            onChange={e => setMoveToStage(e.target.value)}
                            style={{
                              width: '100%', padding: '5px 8px', borderRadius: 6,
                              border: '1px solid #D1D5DB', fontSize: 12, fontFamily: 'inherit',
                              direction: isRtl ? 'rtl' : 'ltr'
                            }}
                          >
                            {stages.filter(s => s.key !== stageKey).map(s => (
                              <option key={s.key} value={s.key}>
                                {isRtl ? s.labelAr : s.labelEn}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={confirmDeleteStage} style={{
                          flex: 1, padding: '5px 0', borderRadius: 6, background: '#EF4444',
                          color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12,
                          fontWeight: 600, fontFamily: 'inherit'
                        }}>
                          {isRtl ? 'حذف' : 'Delete'}
                        </button>
                        <button onClick={() => setDeletingStageKey(null)} style={{
                          flex: 1, padding: '5px 0', borderRadius: 6, background: '#F3F4F6',
                          color: '#374151', border: 'none', cursor: 'pointer', fontSize: 12,
                          fontWeight: 600, fontFamily: 'inherit'
                        }}>
                          {isRtl ? 'إلغاء' : 'Cancel'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      marginBottom: 12, padding: '0 4px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 8, height: 8, borderRadius: '50%', background: stageObj.color
                        }} />
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1A2E' }}>
                          {lang === 'ar' ? stageObj.labelAr : stageObj.labelEn}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span className="font-mono" style={{
                          fontSize: 12, fontWeight: 600, color: '#6B7280',
                          background: '#E5E7EB', borderRadius: 10, padding: '2px 8px'
                        }}>
                          {stageDeals.length}
                        </span>
                        <button onClick={() => startEditStage(stageObj)} style={{
                          background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF',
                          padding: 2, display: 'flex', alignItems: 'center', borderRadius: 4,
                          opacity: 0.5
                        }}
                          onMouseEnter={e => e.currentTarget.style.opacity = 1}
                          onMouseLeave={e => e.currentTarget.style.opacity = 0.5}
                        >
                          <IconEdit size={13} />
                        </button>
                        {stages.length > 1 && (
                          <button onClick={() => startDeleteStage(stageKey)} style={{
                            background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF',
                            padding: 2, display: 'flex', alignItems: 'center', borderRadius: 4,
                            opacity: 0.5
                          }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.color = '#EF4444'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = 0.5; e.currentTarget.style.color = '#9CA3AF'; }}
                          >
                            <IconTrash size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Deal cards */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {stageDeals.map((deal, i) => {
                      const agent = AGENTS[deal.agent];
                      const isExpanded = expandedDeal === deal._id;
                      const dealPriority = PRIORITIES.find(p => p.key === deal.priority) || PRIORITIES[0];
                      return (
                        <Draggable key={deal._id} draggableId={deal._id} index={i}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => setExpandedDeal(isExpanded ? null : deal._id)}
                              style={{
                                ...provided.draggableProps.style,
                                background: snapshot.isDragging ? '#FAFAFE' : '#fff',
                                borderRadius: 10, padding: '12px 14px',
                                border: `1px solid ${isExpanded ? stageObj.color : snapshot.isDragging ? '#5B2D8E' : '#E5E7EB'}`,
                                cursor: 'grab',
                                borderLeft: lang !== 'ar' ? `3px solid ${dealPriority.color}` : undefined,
                                borderRight: lang === 'ar' ? `3px solid ${dealPriority.color}` : undefined,
                                boxShadow: snapshot.isDragging ? '0 8px 24px rgba(91,45,142,0.18)' : 'none',
                                opacity: snapshot.isDragging ? 0.95 : 1,
                                marginBottom: 8
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <div style={{ fontWeight: 600, fontSize: 14 }}>
                                  {lang === 'ar' ? deal.nameAr : deal.nameEn}
                                </div>
                                <span style={{
                                  fontSize: 10, fontWeight: 700, color: dealPriority.color,
                                  background: `${dealPriority.color}14`, padding: '2px 8px',
                                  borderRadius: 10, letterSpacing: '0.04em',
                                  border: `1px solid ${dealPriority.color}30`
                                }}>
                                  {lang === 'ar' ? dealPriority.labelAr : dealPriority.labelEn}
                                </span>
                              </div>
                              <div className="font-mono" style={{
                                fontSize: 16, fontWeight: 700, color: stageObj.color, marginBottom: 6
                              }}>
                                {lang === 'ar' ? deal.valueAr : deal.valueEn}
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                                  color: agent?.color, fontWeight: 500
                                }}>
                                  <IconChartArrowsVertical size={12} />
                                  {lang === 'ar' ? agent?.nameAr : agent?.name}
                                </div>
                                <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                                  {lang === 'ar' ? deal.dateAr : deal.dateEn}
                                </span>
                              </div>

                              {/* View details CTA */}
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedDealId(deal._id); }}
                                style={{
                                  marginTop: 8, width: '100%', padding: '6px 0',
                                  borderRadius: 6, background: '#F7F2FC', border: '1px solid #E8DCF5',
                                  color: '#5B2D8E', fontSize: 12, fontWeight: 600,
                                  cursor: 'pointer', fontFamily: 'inherit',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5
                                }}
                              >
                                <IconEye size={13} />
                                {lang === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                              </button>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {colProvided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          );
        })}

        {/* Add Stage button */}
        <button
          onClick={addNewStage}
          style={{
            minWidth: 180, borderRadius: 12, padding: '12px',
            border: '2px dashed #D1D5DB', background: 'transparent',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 8, cursor: 'pointer', color: '#9CA3AF', transition: 'all 0.2s',
            flexShrink: 0
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#5B2D8E'; e.currentTarget.style.color = '#5B2D8E'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#D1D5DB'; e.currentTarget.style.color = '#9CA3AF'; }}
        >
          <IconPlus size={20} />
          <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
            {isRtl ? 'إضافة مرحلة' : 'Add Stage'}
          </span>
        </button>
      </div>
      </DragDropContext>

      {selectedDeal && (
        <DealSlideOver
          deal={selectedDeal}
          lang={lang}
          stages={stages}
          onClose={() => setSelectedDealId(null)}
          onAddNote={handleAddNote}
          onUpdateDeal={handleUpdateDeal}
        />
      )}
      </>
      )}
    </div>
  );
}
